import React, { useCallback, useEffect, useRef, useState } from "react";
import { Form } from "react-final-form";

import { ISubmitValuationAdapter } from "./adapters";
import { getVehicleDetails } from "./api/get-vehicle-details";
import { Header } from "./components/Header";
import { LoadingSkeleton } from "./components/LoadingSkeleton";
import { VehicleHeader } from "./components/VehicleHeader";
import { ConditionsPage } from "./pages/ConditionsPage";
import { EscalationPage } from "./pages/EscalationPage";
import { ExcludedFromValuationPage } from "./pages/ExcludedFromValuationPage";
import { MechanicalAndElectricalPage } from "./pages/MechanicalAndElectricalPage";
import { ModificationsPage } from "./pages/ModificationsPage";
import { QualificationsPage } from "./pages/QualificationsPage";
import { ServiceHistoryPage } from "./pages/ServiceHistoryPage";
import { SubmittedPage } from "./pages/SubmittedPage";
import { SummaryPage } from "./pages/SummaryPage";
import { ValuationResponse } from "./types/apis";
import { AppInputContextState } from "./types/context";
import { EscalateValuation, FormData, FormResources } from "./types/formData";
import { Navigation } from "./types/navigation";
import { validateContextSchema } from "./utils/validateSchema";
import { excludeFromValuation } from "./utils/vehicleCheckHelpers";
import {
  useValuationManagerContext,
  useValuationManagerDispatch,
} from "./ValuationManagerContext";

interface ValuationManagerFormProps {
  formResources: FormResources;
  appInput: AppInputContextState;
  submitValuationAdapter: ISubmitValuationAdapter;
}

export const ValuationManagerForm: React.FC<ValuationManagerFormProps> = ({
  formResources,
  appInput,
  submitValuationAdapter,
}: ValuationManagerFormProps) => {
  const dispatch = useValuationManagerDispatch();
  const [submitRetryError, setSubmitRetryError] = useState<boolean>(false);
  const [, setEscalateRetryError] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const { pageName, valuation, vehicleDetails } = useValuationManagerContext();
  const submitAttempts = useRef(0);
  const escalateAttempts = useRef(0);

  const escalateValuation: EscalateValuation = useCallback(
    async (
      reason: string,
      details: string,
      formValues: FormData,
      valuation?: ValuationResponse,
    ) => {
      if (dispatch) {
        dispatch({
          type: "UpdateValuationManagerContext",
          data: {
            escalation: { reason: reason, details: details },
            pageName: Navigation.Escalation,
          },
        });
      }
      try {
        const redirectUrl = await submitValuationAdapter.escalateValuation(
          appInput,
          reason,
          details,
          formValues,
          vehicleDetails?.checks.checks || [],
          valuation,
        );
        if (dispatch) {
          dispatch({
            type: "UpdateValuationManagerContext",
            data: {
              redirectUrl: redirectUrl,
            },
          });
        }
        setEscalateRetryError(false);
      } catch (error: unknown) {
        escalateAttempts.current++;
        if (escalateAttempts.current === 2) {
          if (error instanceof Error) {
            // TODO what do we do here?
          }
        } else {
          setEscalateRetryError(true);
        }
      }
    },
    [appInput, submitValuationAdapter, dispatch, vehicleDetails],
  );

  const setPageName = (page: Navigation) => {
    if (dispatch) {
      dispatch({
        type: "UpdateValuationManagerContext",
        data: {
          pageName: page,
        },
      });
    }
  };

  useEffect(() => {
    validateContextSchema(appInput);
    const getVehicleDetailsFromVRMandMileage = async (
      vrm: string,
      mileage: number,
      jwt: string,
    ): Promise<void> => {
      try {
        const response = await getVehicleDetails(vrm, mileage, jwt);
        if (dispatch) {
          dispatch({
            type: "UpdateValuationManagerContext",
            data: {
              appInput: appInput,
              vehicleDetails: response,
            },
          });
        }
      } catch (error: unknown) {
        if (error instanceof Error) {
          escalateValuation("Unable to load vehicle details.", error.message, {});
        }
      } finally {
        setLoading(false);
      }
    };
    if (!vehicleDetails) {
      getVehicleDetailsFromVRMandMileage(
        appInput.vrm,
        appInput.mileage,
        appInput.jwt,
      );
    }
  }, [appInput, dispatch, escalateValuation, setLoading, vehicleDetails]);

  const onSubmit = async (values: FormData) => {
    if (!valuation) {
      // TODO what to do here
      return;
    }
    try {
      const redirectUrl = await submitValuationAdapter.submitValuation(
        appInput,
        valuation,
        values,
      );
      if (dispatch) {
        dispatch({
          type: "UpdateValuationManagerContext",
          data: {
            redirectUrl: redirectUrl,
            pageName: Navigation.Submitted,
          },
        });
      }
      setSubmitRetryError(false);
    } catch (error: unknown) {
      submitAttempts.current++;
      if (submitAttempts.current === 2) {
        if (error instanceof Error) {
          escalateValuation(
            "Unable to submit valuation",
            error.message,
            values,
            valuation,
          );
        } else {
          escalateValuation("Unable to submit valuation", "", values, valuation);
        }
      }
      setSubmitRetryError(true);
    }
  };

  if (loading) {
    return <LoadingSkeleton />;
  }

  if (vehicleDetails && excludeFromValuation(vehicleDetails.checks.checks)) {
    return <ExcludedFromValuationPage formResources={formResources} />;
  }

  return (
    <>
      <Header />
      <VehicleHeader />
      <Form
        onSubmit={onSubmit}
        render={({ handleSubmit }) => (
          <form
            id="vmu-form"
            onSubmit={handleSubmit}
            data-testid="valuation-manager-form"
          >
            {pageName === Navigation.Qualifications && (
              <QualificationsPage
                setPageName={setPageName}
                formResources={formResources}
              />
            )}
            {pageName === Navigation.Modifications && (
              <ModificationsPage
                setPageName={setPageName}
                formResources={formResources}
              />
            )}
            {pageName === Navigation.ServiceHistory && (
              <ServiceHistoryPage
                setPageName={setPageName}
                formResources={formResources}
              />
            )}
            {pageName === Navigation.Conditions && (
              <ConditionsPage
                setPageName={setPageName}
                formResources={formResources}
              />
            )}
            {pageName === Navigation.MechanicalAndElectrical && (
              <MechanicalAndElectricalPage
                setPageName={setPageName}
                formResources={formResources}
              />
            )}
            {pageName === Navigation.Summary && (
              <SummaryPage
                setPageName={setPageName}
                formResources={formResources}
                escalateValuation={escalateValuation}
                submitRetryError={submitRetryError}
              />
            )}
            {pageName === Navigation.Escalation && (
              <EscalationPage formResources={formResources} />
            )}
            {pageName === Navigation.Submitted && (
              <SubmittedPage formResources={formResources} />
            )}
          </form>
        )}
      />
    </>
  );
};
